import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Moment from "moment";
import { toast } from "react-toastify";
import { CollectionsApi } from "../../services/api/api";

function displayName(entry) {
  return entry?.email || entry?.username || entry?.userId || "Unknown";
}

function formatDate(value) {
  if (!value) return "—";
  const asString = String(value);
  const momentValue = /^\d+$/.test(asString)
    ? Moment.unix(parseInt(asString, 10))
    : Moment(value);
  return momentValue.isValid() ? momentValue.format("MMM DD, YYYY") : asString;
}

function CollectionAccessDialog({
  open,
  onClose,
  collection,
  activeLinks,
  token,
  onUpdated,
}) {
  const [targetUserId, setTargetUserId] = useState("");
  const [accessType, setAccessType] = useState("distribution");
  const [grantLoading, setGrantLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState({});

  const collectionId = collection?.collectionId || collection?.relId;
  const linkedAccounts = collection?.linkedAccounts || [];
  const hasActiveLinks = activeLinks.length > 0;
  const activeUserMap = useMemo(
    () => new Map(activeLinks.map((l) => [l.userId, l])),
    [activeLinks]
  );

  const resolveToken = async () => {
    const t = typeof token === "function" ? await token() : token;
    if (!t || typeof t !== "string") throw new Error("No valid token");
    return t;
  };

  const handleGrant = async () => {
    if (!targetUserId || !collectionId) return;
    setGrantLoading(true);
    try {
      const t = await resolveToken();
      const result = await CollectionsApi.linkCollection(
        t,
        collectionId,
        targetUserId,
        accessType
      );
      if (result.success) {
        toast.success("Access granted");
        setTargetUserId("");
        if (typeof onUpdated === "function") await onUpdated();
      } else {
        toast.error(result.message || "Failed to grant access");
      }
    } catch (err) {
      toast.error("Failed to grant access: " + err.message);
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRemove = async (linkedUserId) => {
    if (!collectionId) return;
    setRemoveLoading((prev) => ({ ...prev, [linkedUserId]: true }));
    try {
      const t = await resolveToken();
      const result = await CollectionsApi.unlinkCollection(
        t,
        collectionId,
        linkedUserId
      );
      if (result.success) {
        toast.success("Access revoked");
        if (typeof onUpdated === "function") await onUpdated();
      } else {
        toast.error(result.message || "Failed to revoke access");
      }
    } catch (err) {
      toast.error("Failed to revoke access: " + err.message);
    } finally {
      setRemoveLoading((prev) => ({ ...prev, [linkedUserId]: false }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Access — {collection?.title || "Collection"}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="linked-account-select-label">
              Linked Account
            </InputLabel>
            <Select
              labelId="linked-account-select-label"
              label="Linked Account"
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              disabled={grantLoading || !hasActiveLinks}
            >
              <MenuItem value="">
                <em>Select account...</em>
              </MenuItem>
              {activeLinks.map((link) => (
                <MenuItem key={link.userId} value={link.userId}>
                  {displayName(link)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={accessType}
            exclusive
            onChange={(e, value) => {
              if (value) setAccessType(value);
            }}
            aria-label="Access level"
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value="distribution">Distribution</ToggleButton>
            <ToggleButton value="full">Full</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            onClick={handleGrant}
            disabled={!targetUserId || grantLoading || !hasActiveLinks}
            fullWidth
          >
            Grant Access
          </Button>

          {!hasActiveLinks && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              You do not have any active account-level links. Link an account first from the Linked Accounts page.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Shared With
        </Typography>

        {linkedAccounts.length === 0 ? (
          <Typography color="text.secondary">
            This collection is not shared with any linked accounts.
          </Typography>
        ) : (
          <List dense>
            {linkedAccounts.map((entry) => {
              const resolved = activeUserMap.get(entry.userId) || entry;
              return (
              <ListItem
                key={entry.userId}
                divider
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label={`Revoke access for ${displayName(resolved)}`}
                    onClick={() => handleRemove(entry.userId)}
                    disabled={removeLoading[entry.userId]}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {displayName(resolved)}
                      </Typography>
                      <Chip
                        label={entry.type}
                        size="small"
                        color={entry.type === "full" ? "primary" : "default"}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={`Granted on ${formatDate(entry.linkedAt)}`}
                />
              </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default CollectionAccessDialog;
